import { useEffect, useRef } from "react";
import * as d3 from "d3";
import type { MapDestination, MapTransit } from "../types";
import {
  drawBaseLand,
  getDestinationDisplayLabel,
  groupDestinationsByCountry,
  loadWorldGeojson,
  MAP_HEIGHT,
  MAP_WIDTH,
  scaleValue,
} from "./mapShared";

type Props = {
  destinations: MapDestination[];
  transits?: MapTransit[];
  uiScale?: number;
  onSelectDestination?: (consignmentIds: string[]) => void;
};

// --- helpers de style
const SZ = scaleValue;

const WIDTH = MAP_WIDTH;
const HEIGHT = MAP_HEIGHT + 80;
const DONUT_RADIUS = 18;
const TRANSIT_CLUSTER_RADIUS_PX = 18;
const STRAIGHT_ROUTE_MAX_LENGTH_PX = 90;
const DEFAULT_MIN_TRANSIT_VOLUME = 1;
const DEFAULT_TOP_TRANSIT_ROUTES = 9999;

type DonutDatum = {
  destination: { lat: number; lon: number };
  key: string;
  x: number;
  y: number;
  consignmentCount: number;
  consignmentIds: string[];
  compliant: number;
  nonCompliant: number;
  label: string;
  displayName: string;
};

type ProjectedTransitPoint = {
  key: string;
  transit: MapTransit;
  label: string | null;
  lat: number;
  lon: number;
  x: number;
  y: number;
};

type TransitCluster = {
  id: string;
  x: number;
  y: number;
  points: ProjectedTransitPoint[];
};

type AggregatedTransitRoute = {
  id: string;
  fromCluster: TransitCluster;
  toCluster: TransitCluster;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  count: number;
  consignmentIds: string[];
  rawTransits: MapTransit[];
  parallelIndex: number;
  parallelTotal: number;
};

type TransitRenderResult = {
  visibleRoutes: AggregatedTransitRoute[];
  visibleClusters: TransitCluster[];
};

type MapPoint = {
  x: number;
  y: number;
};

type MapBounds = MapPoint & {
  width: number;
  height: number;
};

type LabelBox = MapBounds;

export function MapPanel({ destinations, transits, uiScale = 1, onSelectDestination }: Props) {
  const ref = useRef<SVGSVGElement | null>(null);

  useEffect(() => {
    if (!ref.current) return;

    let alive = true;
    const ac = new AbortController();

    const svg = d3.select(ref.current as SVGSVGElement);
    svg.selectAll("*").remove();
    svg
      .attr("viewBox", `0 0 ${WIDTH} ${HEIGHT}`)
      .style("width", "100%")
      .style("height", `${HEIGHT}px`);

    (async () => {
      // charge le fond du monde (annulable)
      const geo = await loadWorldGeojson(ac.signal);
      if (!geo || !alive) return;

      // conteneurs : legend fixe, tout le reste bouge
      const root = svg.append("g").attr("class", "map-root");
      const movable = root.append("g").attr("class", "map-movable");

      const projection = d3.geoMercator().fitExtent(
        [
          [30, 30],
          [WIDTH - 30, HEIGHT - 40],
        ],
        geo,
      );
      // fond (bouge avec le zoom)
      drawBaseLand(movable, geo, projection, {
        fill: "#f4f8ee",
        stroke: "#d4e2f5",
        strokeWidth: 0.45,
      });
      const dataLayer = movable.append("g").attr("class", "map-data-layer");

      const destinationsByCountry = groupDestinationsByCountry(destinations);
      const completedTransits = completeDestinationRoutes(transits ?? [], destinationsByCountry);
      const projectedDestinations = projectMapDestinations(destinationsByCountry, projection);
      const renderDataLayer = (clusterZoomLevel: number) => {
        dataLayer.selectAll("*").remove();
        const transitRender = drawTransits(
          dataLayer,
          completedTransits,
          projection,
          DEFAULT_MIN_TRANSIT_VOLUME,
          DEFAULT_TOP_TRANSIT_ROUTES,
          true,
          clusterZoomLevel,
          uiScale,
          onSelectDestination,
        );
        drawTransitClusterMarkers(dataLayer, transitRender.visibleRoutes, uiScale, onSelectDestination);
        drawTransitClusterLabels(dataLayer, transitRender.visibleRoutes, projectedDestinations, uiScale);
        drawDonuts(dataLayer, destinationsByCountry, projection, uiScale, onSelectDestination);
        return transitRender;
      };

      let currentClusterZoomLevel = 1;
      const initialRender = renderDataLayer(currentClusterZoomLevel);
      const fitBounds = getMapFitBounds(collectMapFitPoints(
        initialRender.visibleRoutes,
        projectedDestinations,
      ));

      const geolocatedDestinations = destinationsByCountry.filter((destination) => destination.destination);
      if (destinationsByCountry.length > 0 && geolocatedDestinations.length === 0) {
        root
          .append("text")
          .attr("x", WIDTH / 2)
          .attr("y", HEIGHT / 2 - 6)
          .attr("text-anchor", "middle")
          .attr("font-size", SZ(16, uiScale))
          .attr("font-weight", 700)
          .attr("fill", "#334155")
          .text("Aucun point géolocalisé disponible");

        root
          .append("text")
          .attr("x", WIDTH / 2)
          .attr("y", HEIGHT / 2 + 18)
          .attr("text-anchor", "middle")
          .attr("font-size", SZ(12, uiScale))
          .attr("fill", "#64748b")
          .text("Les villes existent mais aucune coordonnée latitude/longitude n'est exploitable.");
      }

      // éviter multiples handlers de zoom si l’effet rerend
      svg.on(".zoom", null);
      addZoom(svg, movable, fitBounds, (zoomScale) => {
        const nextClusterZoomLevel = getClusterZoomLevel(zoomScale);
        if (nextClusterZoomLevel === currentClusterZoomLevel) return;

        currentClusterZoomLevel = nextClusterZoomLevel;
        renderDataLayer(currentClusterZoomLevel);
        lockMapItemsScale(movable, zoomScale);
      });

    })();

    // cleanup : annule le fetch & invalide l’effet
    return () => {
      alive = false;
      ac.abort();
    };
  }, [destinations, onSelectDestination, transits, uiScale]);

  return (
    <div className="map-flow-panel">
      <svg ref={ref} role="img" aria-label="Carte mondiale des expéditions" />
      <div className="map-flow-status-legend">
        <span><i className="map-flow-dot map-flow-dot--ok" />Compliant</span>
        <span><i className="map-flow-dot map-flow-dot--ko" />Non compliant</span>
      </div>
    </div>
  );
}

function drawTransits(
  container: d3.Selection<SVGGElement, unknown, null, undefined>,
  transits: MapTransit[],
  projection: d3.GeoProjection,
  minVolume: number,
  routeLimit: number,
  showClusters: boolean,
  clusterZoomLevel: number,
  s = 1,
  onSelectDestination?: (consignmentIds: string[]) => void,
): TransitRenderResult {
  const group = container.append("g").attr("class", "map-transits");
  const projectedPoints = projectTransitPoints(transits, projection);
  const clusterRadius = showClusters ? TRANSIT_CLUSTER_RADIUS_PX / clusterZoomLevel : 0;
  const clusters = clusterTransitPoints(projectedPoints, clusterRadius);
  const routes = aggregateTransitRoutes(clusters);
  const visibleRoutes = resolveVisibleTransitRoutes(routes, minVolume, routeLimit);
  const maxCount = d3.max(visibleRoutes, (route) => route.count) ?? 1;
  const strokeWidth = d3.scaleSqrt().domain([1, maxCount]).range([SZ(1.8, s), SZ(10, s)]);
  const opacity = d3.scaleSqrt().domain([1, maxCount]).range([0.68, 0.95]);

  visibleRoutes.forEach((route) => {
    const label = formatAggregatedTransitLabel(route);

    const path = group
      .append("path")
      .attr("class", "map-transit")
      .attr("data-key", route.id)
      .attr("d", createCurvedTransitPath(route, s))
      .attr("fill", "none")
      .attr("stroke", "#1d4ed8")
      .attr("stroke-width", strokeWidth(route.count))
      .attr("stroke-linecap", "round")
      .attr("opacity", opacity(route.count))
      .attr("vector-effect", "non-scaling-stroke");

    path.append("title").text(label);

    if (onSelectDestination) {
      path
        .style("cursor", "pointer")
        .on("click", (event) => {
          event.stopPropagation();
          onSelectDestination(route.consignmentIds);
        });
    }
  });

  return {
    visibleRoutes,
    visibleClusters: collectVisibleTransitClusters(visibleRoutes),
  };
}

type ProjectedMapDestination = {
  x: number;
  y: number;
  destination: MapDestination;
};

function projectMapDestinations(destinations: MapDestination[], projection: d3.GeoProjection): ProjectedMapDestination[] {
  return destinations
    .map((destination) => {
      if (!destination.destination) return null;
      const projected = projection([destination.destination.lon, destination.destination.lat]);
      if (!projected) return null;
      return { x: projected[0], y: projected[1], destination };
    })
    .filter((destination): destination is ProjectedMapDestination => destination !== null);
}

function collectMapFitPoints(
  routes: AggregatedTransitRoute[],
  destinations: ProjectedMapDestination[],
): MapPoint[] {
  const points: MapPoint[] = [];

  for (const route of routes) {
    points.push({ x: route.fromX, y: route.fromY });
    points.push({ x: route.toX, y: route.toY });
  }

  for (const destination of destinations) {
    points.push({ x: destination.x, y: destination.y });
  }

  return points;
}

function getMapFitBounds(points: MapPoint[]): MapBounds | null {
  const validPoints = points.filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y));
  if (validPoints.length === 0) return null;

  const xs = validPoints.map((point) => point.x);
  const ys = validPoints.map((point) => point.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  return {
    x: minX,
    y: minY,
    width: Math.max(maxX - minX, 1),
    height: Math.max(maxY - minY, 1),
  };
}

function completeDestinationRoutes(transits: MapTransit[], destinations: MapDestination[]): MapTransit[] {
  const destinationsByConsignmentId = buildDestinationsByConsignmentId(destinations);
  const outgoingCoordinatesByConsignmentId = buildOutgoingCoordinatesByConsignmentId(transits);
  const terminalPointsByConsignmentId = buildTerminalPointsByConsignmentId(transits, outgoingCoordinatesByConsignmentId);
  const additionalTransitMap = new Map<string, MapTransit>();

  destinationsByConsignmentId.forEach((destination, consignmentId) => {
    if (!destination?.destination) return;
    const hasTransit = outgoingCoordinatesByConsignmentId.has(consignmentId);
    const terminalPoint = terminalPointsByConsignmentId.get(consignmentId);
    if (hasTransit && !terminalPoint) return;

    const from = terminalPoint ?? toTransitPoint(destination.origin);
    if (!from || isSameMapPoint(from, destination.destination)) return;

    const routeType = terminalPoint ? "final" : "direct";

    const key = [
      routeType,
      coordinateKey(from),
      coordinateKey(destination.destination),
    ].join("|");
    const existing = additionalTransitMap.get(key);
    if (existing) {
      existing.consignmentIds = uniqueStrings([...existing.consignmentIds, consignmentId]);
      existing.count = existing.consignmentIds.length;
      return;
    }

    additionalTransitMap.set(key, {
      key,
      from,
      to: {
        lat: destination.destination.lat,
        lon: destination.destination.lon,
        label: formatDestinationDisplayName(destination),
      },
      count: 1,
      consignmentIds: [consignmentId],
    });
  });

  return [...transits, ...additionalTransitMap.values()];
}

function toTransitPoint(point: MapDestination["origin"]): MapTransit["from"] | null {
  if (!point) return null;
  return { ...point, label: null };
}

function buildDestinationsByConsignmentId(destinations: MapDestination[]): Map<string, MapDestination> {
  const map = new Map<string, MapDestination>();

  for (const destination of destinations) {
    for (const consignmentId of destination.consignmentIds) {
      const trimmed = consignmentId.trim();
      if (trimmed && !map.has(trimmed)) {
        map.set(trimmed, destination);
      }
    }
  }

  return map;
}

function buildOutgoingCoordinatesByConsignmentId(transits: MapTransit[]): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>();

  for (const transit of transits) {
    const key = coordinateKey(transit.from);
    for (const consignmentId of transit.consignmentIds) {
      const trimmed = consignmentId.trim();
      if (!trimmed) continue;
      const coordinates = map.get(trimmed) ?? new Set<string>();
      coordinates.add(key);
      map.set(trimmed, coordinates);
    }
  }

  return map;
}

function buildTerminalPointsByConsignmentId(
  transits: MapTransit[],
  outgoingCoordinatesByConsignmentId: Map<string, Set<string>>,
): Map<string, MapTransit["to"]> {
  const map = new Map<string, MapTransit["to"]>();

  for (const transit of transits) {
    const key = coordinateKey(transit.to);
    for (const consignmentId of transit.consignmentIds) {
      const trimmed = consignmentId.trim();
      if (!trimmed) continue;
      const outgoingCoordinates = outgoingCoordinatesByConsignmentId.get(trimmed);
      if (outgoingCoordinates?.has(key)) continue;
      map.set(trimmed, transit.to);
    }
  }

  return map;
}

function coordinateKey(point: { lat: number; lon: number }): string {
  return `${point.lat.toFixed(4)}|${point.lon.toFixed(4)}`;
}

function isSameMapPoint(first: { lat: number; lon: number }, second: { lat: number; lon: number }): boolean {
  return coordinateKey(first) === coordinateKey(second);
}

function projectTransitPoints(transits: MapTransit[], projection: d3.GeoProjection): ProjectedTransitPoint[] {
  const points: ProjectedTransitPoint[] = [];

  transits.forEach((transit) => {
    addProjectedTransitPoint(points, transit, "from", projection);
    addProjectedTransitPoint(points, transit, "to", projection);
  });

  return points;
}

function addProjectedTransitPoint(
  points: ProjectedTransitPoint[],
  transit: MapTransit,
  side: "from" | "to",
  projection: d3.GeoProjection,
) {
  const point = transit[side];
  const projected = projection([point.lon, point.lat]);
  if (!projected) return;

  const [x, y] = projected;
  points.push({
    key: `${transit.key}:${side}`,
    transit,
    label: point.label,
    lat: point.lat,
    lon: point.lon,
    x,
    y,
  });
}

function clusterTransitPoints(points: ProjectedTransitPoint[], radiusPx: number): TransitCluster[] {
  const clusters: TransitCluster[] = [];

  for (const point of points) {
    const cluster = findNearbyCluster(clusters, point, radiusPx);
    if (cluster) {
      addPointToCluster(cluster, point);
    } else {
      clusters.push({
        id: `cluster-${clusters.length}`,
        x: point.x,
        y: point.y,
        points: [point],
      });
    }
  }

  return clusters;
}

function findNearbyCluster(
  clusters: TransitCluster[],
  point: ProjectedTransitPoint,
  radiusPx: number,
): TransitCluster | null {
  for (const cluster of clusters) {
    const dx = point.x - cluster.x;
    const dy = point.y - cluster.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    if (distance <= radiusPx) return cluster;
  }
  return null;
}

function addPointToCluster(cluster: TransitCluster, point: ProjectedTransitPoint) {
  cluster.points.push(point);
  const total = cluster.points.length;
  const sum = cluster.points.reduce(
    (acc, item) => ({
      x: acc.x + item.x,
      y: acc.y + item.y,
    }),
    { x: 0, y: 0 },
  );
  cluster.x = sum.x / total;
  cluster.y = sum.y / total;
}

function aggregateTransitRoutes(clusters: TransitCluster[]): AggregatedTransitRoute[] {
  const pointClusterMap = buildPointClusterMap(clusters);
  const routeMap = new Map<string, AggregatedTransitRoute>();
  const processedTransitKeys = new Set<string>();

  for (const cluster of clusters) {
    for (const point of cluster.points) {
      if (processedTransitKeys.has(point.transit.key)) continue;
      processedTransitKeys.add(point.transit.key);

      const fromCluster = pointClusterMap.get(`${point.transit.key}:from`);
      const toCluster = pointClusterMap.get(`${point.transit.key}:to`);
      if (!fromCluster || !toCluster) continue;
      if (fromCluster.id === toCluster.id && !isFinalTransit(point.transit)) continue;

      const key = `${fromCluster.id}->${toCluster.id}`;
      const route = routeMap.get(key);
      if (route) {
        mergeTransitIntoRoute(route, point.transit);
      } else {
        routeMap.set(key, createAggregatedTransitRoute(key, fromCluster, toCluster, point.transit));
      }
    }
  }

  return assignParallelTransitIndexes(Array.from(routeMap.values()));
}

function isFinalTransit(transit: MapTransit): boolean {
  return transit.key.startsWith("final|");
}

function buildPointClusterMap(clusters: TransitCluster[]): Map<string, TransitCluster> {
  const map = new Map<string, TransitCluster>();
  for (const cluster of clusters) {
    for (const point of cluster.points) {
      map.set(point.key, cluster);
    }
  }
  return map;
}

function createAggregatedTransitRoute(
  id: string,
  fromCluster: TransitCluster,
  toCluster: TransitCluster,
  transit: MapTransit,
): AggregatedTransitRoute {
  return {
    id,
    fromCluster,
    toCluster,
    fromX: fromCluster.x,
    fromY: fromCluster.y,
    toX: toCluster.x,
    toY: toCluster.y,
    count: transit.count,
    consignmentIds: uniqueStrings(transit.consignmentIds),
    rawTransits: [transit],
    parallelIndex: 0,
    parallelTotal: 1,
  };
}

function mergeTransitIntoRoute(
  route: AggregatedTransitRoute,
  transit: MapTransit,
) {
  route.count += transit.count;
  route.consignmentIds = uniqueStrings([...route.consignmentIds, ...transit.consignmentIds]);
  route.rawTransits.push(transit);
}

function assignParallelTransitIndexes(routes: AggregatedTransitRoute[]): AggregatedTransitRoute[] {
  const groups = new Map<string, AggregatedTransitRoute[]>();

  for (const route of routes) {
    const key = [route.fromCluster.id, route.toCluster.id].sort().join("<->");
    const group = groups.get(key) ?? [];
    group.push(route);
    groups.set(key, group);
  }

  groups.forEach((group) => {
    group.forEach((route, index) => {
      route.parallelIndex = index;
      route.parallelTotal = group.length;
    });
  });

  return routes;
}

function resolveVisibleTransitRoutes(
  routes: AggregatedTransitRoute[],
  minVolume: number,
  limit: number,
): AggregatedTransitRoute[] {
  return routes
    .filter((route) => route.count >= minVolume)
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
}

function drawTransitClusterLabels(
  container: d3.Selection<SVGGElement, unknown, null, undefined>,
  routes: AggregatedTransitRoute[],
  destinations: ProjectedMapDestination[],
  s: number,
) {
  const terminalClusterIds = collectTerminalTransitClusterIds(routes);
  const clusters = collectVisibleTransitClusters(routes).filter((cluster) => {
    if (terminalClusterIds.has(cluster.id)) return false;
    if (isClusterNearDestination(cluster, destinations, s)) return false;
    return true;
  });
  const group = container.append("g").attr("class", "map-transit-cluster-labels");
  const placedLabels: LabelBox[] = [];

  clusters.forEach((cluster) => {
    const label = formatClusterLabel(cluster, "Hub");
    const width = Math.max(SZ(42, s), Math.min(SZ(88, s), label.length * SZ(4.8, s) + SZ(12, s)));
    const height = SZ(16, s);
    const box = resolveClusterLabelBox(cluster, width, height, placedLabels, s);
    placedLabels.push(box);

    const item = group
      .append("g")
      .attr("class", "map-transit-cluster-label")
      .attr("data-x", cluster.x)
      .attr("data-y", cluster.y);
    item
      .append("rect")
      .attr("x", box.x)
      .attr("y", box.y)
      .attr("width", width)
      .attr("height", height)
      .attr("rx", SZ(4, s))
      .attr("ry", SZ(4, s));
    item
      .append("text")
      .attr("x", box.x + box.width / 2)
      .attr("y", box.y + SZ(11, s))
      .attr("text-anchor", "middle")
      .text(label);
  });
}

function isClusterNearDestination(
  cluster: TransitCluster,
  destinations: ProjectedMapDestination[],
  s: number,
): boolean {
  const maxDistance = SZ(DONUT_RADIUS + 12, s);

  for (const destination of destinations) {
    const dx = cluster.x - destination.x;
    const dy = cluster.y - destination.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    if (distance <= maxDistance) return true;
  }

  return false;
}

function resolveClusterLabelBox(
  cluster: TransitCluster,
  width: number,
  height: number,
  placedLabels: LabelBox[],
  s: number,
): LabelBox {
  const candidates = getClusterLabelCandidates(cluster, width, height, s);

  for (const candidate of candidates) {
    if (doesLabelTouchCluster(candidate, cluster, s)) continue;
    if (doesLabelOverlapAny(candidate, placedLabels)) continue;
    return candidate;
  }

  for (const candidate of candidates) {
    if (doesLabelTouchCluster(candidate, cluster, s)) continue;
    return candidate;
  }

  return candidates[0];
}

function getClusterLabelCandidates(
  cluster: TransitCluster,
  width: number,
  height: number,
  s: number,
): LabelBox[] {
  const offset = SZ(26, s);
  const above = clampLabelBox({ x: cluster.x - width / 2, y: cluster.y - height - offset, width, height }, s);
  const right = clampLabelBox({ x: cluster.x + offset, y: cluster.y - height / 2, width, height }, s);
  const below = clampLabelBox({ x: cluster.x - width / 2, y: cluster.y + offset, width, height }, s);
  const left = clampLabelBox({ x: cluster.x - width - offset, y: cluster.y - height / 2, width, height }, s);

  if (cluster.x > WIDTH * 0.62) {
    return [left, above, below, right];
  }

  if (cluster.x < WIDTH * 0.38) {
    return [right, above, below, left];
  }

  return [above, right, left, below];
}

function clampLabelBox(box: LabelBox, s: number): LabelBox {
  const margin = SZ(4, s);
  const maxX = WIDTH - box.width - margin;
  const maxY = HEIGHT - box.height - margin;

  return {
    x: Math.max(margin, Math.min(box.x, maxX)),
    y: Math.max(margin, Math.min(box.y, maxY)),
    width: box.width,
    height: box.height,
  };
}

function doesLabelTouchCluster(box: LabelBox, cluster: TransitCluster, s: number): boolean {
  const clearance = SZ(18, s);
  const clusterBox = {
    x: cluster.x - clearance,
    y: cluster.y - clearance,
    width: clearance * 2,
    height: clearance * 2,
  };
  return doBoxesOverlap(box, clusterBox);
}

function doesLabelOverlapAny(box: LabelBox, placedLabels: LabelBox[]): boolean {
  for (const placedLabel of placedLabels) {
    if (doBoxesOverlap(box, placedLabel)) return true;
  }
  return false;
}

function doBoxesOverlap(first: LabelBox, second: LabelBox): boolean {
  const firstRight = first.x + first.width;
  const secondRight = second.x + second.width;
  const firstBottom = first.y + first.height;
  const secondBottom = second.y + second.height;

  if (firstRight <= second.x) return false;
  if (secondRight <= first.x) return false;
  if (firstBottom <= second.y) return false;
  if (secondBottom <= first.y) return false;
  return true;
}

function collectVisibleTransitClusters(routes: AggregatedTransitRoute[]): TransitCluster[] {
  const map = new Map<string, TransitCluster>();

  for (const route of routes) {
    map.set(route.fromCluster.id, route.fromCluster);
    map.set(route.toCluster.id, route.toCluster);
  }

  return Array.from(map.values());
}

function drawTransitClusterMarkers(
  container: d3.Selection<SVGGElement, unknown, null, undefined>,
  routes: AggregatedTransitRoute[],
  s: number,
  onSelectDestination?: (consignmentIds: string[]) => void,
) {
  const clusters = collectVisibleTransitClusters(routes);
  const terminalClusterIds = collectTerminalTransitClusterIds(routes);
  const group = container.append("g").attr("class", "map-transit-cluster-markers");

  clusters.forEach((cluster) => {
    const label = formatClusterLabel(cluster, "Hub");
    const isTerminal = terminalClusterIds.has(cluster.id);
    const consignmentIds = collectClusterConsignmentIdsFromRoutes(cluster, routes);
    const consignmentCount = consignmentIds.length;
    const radius = Math.max(SZ(9, s), Math.min(SZ(15, s), Math.sqrt(consignmentCount) * SZ(5.5, s)));
    const labelSize = radius >= SZ(13, s) ? SZ(10, s) : SZ(8, s);
    const item = group
      .append("g")
      .attr("class", "map-transit-cluster-marker")
      .classed("is-terminal", isTerminal)
      .attr("data-x", cluster.x)
      .attr("data-y", cluster.y);

    item.append("title").text(`${label}\n${isTerminal ? "Destination finale par événements" : "Hub de transit"}`);
    if (onSelectDestination) {
      item
        .style("cursor", "pointer")
        .on("click", (event) => {
          event.stopPropagation();
          onSelectDestination(consignmentIds);
        });
    }
    item
      .append("circle")
      .attr("cx", cluster.x)
      .attr("cy", cluster.y)
      .attr("r", radius);
    item
      .append("text")
      .attr("x", cluster.x)
      .attr("y", cluster.y)
      .attr("text-anchor", "middle")
      .attr("dominant-baseline", "central")
      .attr("font-size", labelSize)
      .text(formatCenterCount(consignmentCount));
  });
}

function collectTerminalTransitClusterIds(routes: AggregatedTransitRoute[]): Set<string> {
  const sourceIds = new Set(routes.map((route) => route.fromCluster.id));
  const targetIds = new Set(routes.map((route) => route.toCluster.id));
  const terminalIds = new Set<string>();

  targetIds.forEach((id) => {
    if (!sourceIds.has(id)) {
      terminalIds.add(id);
    }
  });

  return terminalIds;
}

function collectClusterConsignmentIdsFromRoutes(
  cluster: TransitCluster,
  routes: AggregatedTransitRoute[],
): string[] {
  const ids = routes
    .filter((route) => route.fromCluster.id === cluster.id || route.toCluster.id === cluster.id)
    .flatMap((route) => route.consignmentIds);
  return uniqueStrings(ids);
}

function createCurvedTransitPath(route: AggregatedTransitRoute, s: number): string {
  const x1 = route.fromX;
  const y1 = route.fromY;
  const x2 = route.toX;
  const y2 = route.toY;
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const length = Math.sqrt(dx * dx + dy * dy) || 1;
  if (length <= SZ(STRAIGHT_ROUTE_MAX_LENGTH_PX, s)) {
    return `M${x1},${y1} L${x2},${y2}`;
  }

  const nx = -dy / length;
  const ny = dx / length;
  const baseCurve = Math.min(SZ(90, s), Math.max(SZ(10, s), length * 0.14));
  const parallelOffset = (route.parallelIndex - (route.parallelTotal - 1) / 2) * SZ(18, s);
  const curveOffset = baseCurve + parallelOffset;
  const cx = mx + nx * curveOffset;
  const cy = my + ny * curveOffset;
  return `M${x1},${y1} Q${cx},${cy} ${x2},${y2}`;
}

function formatAggregatedTransitLabel(route: AggregatedTransitRoute): string {
  const fromLabel = formatClusterLabel(route.fromCluster, "Origine");
  const toLabel = formatClusterLabel(route.toCluster, "Destination");
  const shipmentCount = route.consignmentIds.length;
  return `${fromLabel} → ${toLabel}\nFlux agrégé : ${route.count}\nExpéditions : ${shipmentCount}`;
}

function formatClusterLabel(cluster: TransitCluster, fallback: string): string {
  const labels = uniqueStrings(cluster.points.map((point) => point.label ?? "").filter(Boolean));
  if (labels.length === 0) return fallback;
  if (labels.length <= 3) return labels.join(", ");
  return `${labels.slice(0, 3).join(", ")} +${labels.length - 3}`;
}

function uniqueStrings(values: string[]): string[] {
  return Array.from(new Set(values.filter((value) => value.trim().length > 0)));
}

function drawDonuts(
  container: d3.Selection<SVGGElement, unknown, null, undefined>,
  destinations: MapDestination[],
  projection: d3.GeoProjection,
  s = 1,
  onSelectDestination?: (consignmentIds: string[]) => void,
) {
  const MIN_RADIUS = SZ(16, s);
  const MAX_RADIUS = SZ(28, s);
  const OUTLINE = SZ(4, s);
  const HITBOX = SZ(8, s);
  const SLICE_STROKE = SZ(1.5, s);
  const TOOLTIP_FONT = SZ(12, s);
  const TOOLTIP_W = SZ(190, s), TOOLTIP_H = SZ(35, s);
  const TOOLTIP_RX = SZ(6, s);
  const TOOLTIP_X = -TOOLTIP_W / 2, TOOLTIP_Y = -SZ(50, s);

  const pie = d3.pie<{ name: string; value: number }>()
    .value((d) => d.value)
    .sort(null);

  const color = d3
    .scaleOrdinal<string, string>()
    .domain(["Compliant", "Non compliant", "No data"])
    .range(["#22c55e", "#f97316", "#94a3b8"]);

  const group = container.append("g").attr("class", "map-donuts");
  const visibleDestinations = destinations.filter((destination) => destination.destination);
  const maxConsignmentCount = d3.max(visibleDestinations, (destination) => Math.max(destination.consignmentIds.length, 1)) ?? 1;
  const radiusScale =
    maxConsignmentCount > 1
      ? d3.scaleSqrt<number, number>().domain([1, maxConsignmentCount]).range([MIN_RADIUS, MAX_RADIUS])
      : () => MIN_RADIUS;

  const hideAllTooltips = () => {
    group.selectAll<SVGGElement, unknown>("g.donut-tooltip").style("display", "none");
    group
      .selectAll<SVGCircleElement, unknown>("circle.donut-outline")
      .attr("stroke", "rgba(15,23,42,0.08)")
      .attr("stroke-width", SZ(1, s));
  };

  visibleDestinations
    .forEach((dest) => {
      const [x, y] = projection([dest.destination!.lon, dest.destination!.lat]) ?? [0, 0];
      const consignmentCount = Math.max(dest.consignmentIds.length, 0);
      const destinationLabel = getDestinationDisplayLabel(dest);
      const destinationDisplayName = formatDestinationDisplayName(dest);
      const radius = radiusScale(Math.max(consignmentCount, 1));
      const innerRadius = radius * 0.58;
      const centerLabel = formatCenterCount(consignmentCount);
      const centerFontSize = resolveCenterFontSize(radius, centerLabel);
      const base = [
        { name: "Compliant", value: dest.counts.compliant },
        { name: "Non compliant", value: dest.counts.nonCompliant },
      ];
      const total = base.reduce((sum, item) => sum + item.value, 0);
      const data = total > 0 ? base : [{ name: "No data", value: 1 }];
      const arc = d3
        .arc<d3.PieArcDatum<{ name: string; value: number }>>()
        .innerRadius(innerRadius)
        .outerRadius(radius);

      const donut = group
        .append("g")
        .datum<DonutDatum>({
          key: dest.key,
          destination: dest.destination!,
          x,
          y,
          consignmentCount,
          consignmentIds: dest.consignmentIds,
          compliant: dest.counts.compliant,
          nonCompliant: dest.counts.nonCompliant,
          label: destinationLabel,
          displayName: destinationDisplayName,
        })
        .attr("class", "donut-item")
        .attr("transform", `translate(${x}, ${y}) scale(1)`);

      // Info globale en title (fallback natif)
      donut
        .append("title")
        .text(
          ({ displayName, consignmentCount, compliant, nonCompliant }) =>
            `${displayName}\nExpeditions : ${consignmentCount}\nCompliant : ${compliant}\nNon compliant : ${nonCompliant}`,
        );

      // Tranches
      donut
        .selectAll("path")
        .data(pie(data))
        .join("path")
        .attr("class", "donut-slice")
        .attr("d", arc)
        .attr("fill", (d) => color(d.data.name))
        .attr("stroke", "#ffffff")
        .attr("stroke-width", SLICE_STROKE);

      donut
        .append("circle")
        .attr("class", "donut-center")
        .attr("r", innerRadius)
        .attr("fill", "#ffffff")
        .attr("stroke", "rgba(148,163,184,0.24)")
        .attr("stroke-width", SZ(0.8, s));

      donut
        .append("text")
        .attr("class", "donut-count")
        .attr("text-anchor", "middle")
        .attr("dominant-baseline", "central")
        .attr("fill", "#0f172a")
        .attr("font-size", centerFontSize)
        .attr("font-weight", 700)
        .style("pointer-events", "none")
        .text(centerLabel);

      // Cercle d’accent
      donut
        .append("circle")
        .attr("class", "donut-outline")
        .attr("r", radius + OUTLINE)
        .attr("fill", "transparent")
        .attr("stroke", "rgba(15,23,42,0.08)")
        .attr("stroke-width", SZ(1, s))
        .lower();

      // Tooltip caché par défaut
      const tooltipGroup = donut
        .append("g")
        .attr("class", "donut-tooltip")
        .style("display", "none")
        .attr("pointer-events", "none"); // important: ne pas capter la souris

      tooltipGroup
        .append("rect")
        .attr("x", TOOLTIP_X)
        .attr("y", TOOLTIP_Y)
        .attr("width", TOOLTIP_W)
        .attr("height", TOOLTIP_H)
        .attr("rx", TOOLTIP_RX)
        .attr("ry", TOOLTIP_RX)
        .attr("fill", "#ffffff")
        .attr("stroke", "#cbd5f5")
        .attr("stroke-width", SZ(1.2, s))
        .attr("opacity", 0.95);

      tooltipGroup
        .append("text")
        .attr("x", 0)
        .attr("y", TOOLTIP_Y + SZ(23, s))
        .attr("text-anchor", "middle")
        .attr("fill", "#1f2937")
        .attr("font-size", TOOLTIP_FONT)
        .attr("font-weight", 600)
        .text(destinationDisplayName);

      // Hitbox + handlers d’affichage/masquage du tooltip
      donut
        .append("circle")
        .attr("class", "donut-hitbox")
        .attr("r", radius + HITBOX)
        .attr("fill", "transparent")
        .style("pointer-events", "all")
        .on("mouseenter", function () {
          hideAllTooltips();
          const g = d3.select(this.parentNode as SVGGElement);
          g.raise();
          g.select<SVGGElement>("g.donut-tooltip").style("display", "block").raise();
          g.select<SVGCircleElement>("circle.donut-outline")
            .attr("stroke", "#fb923c")
            .attr("stroke-width", SZ(2.5, s));
        })
        .on("mouseleave", function () {
          hideAllTooltips();
        })
        .on("click", function (event, datum: DonutDatum) {
          event.stopPropagation();
          if (onSelectDestination) {
            onSelectDestination(datum.consignmentIds);
          }
        });
    });
}

function formatCenterCount(value: number): string {
  if (value >= 1000) {
    return d3.format(".2s")(value).replace(".0", "");
  }
  return value.toString();
}

function formatDestinationDisplayName(destination: MapDestination): string {
  const city = destination.destinationCity?.trim();
  const country = formatCountryName(destination.destinationCountry);
  const parts = [city, country].filter((value): value is string => Boolean(value));

  if (parts.length > 0) {
    return parts.join(", ");
  }

  return getDestinationDisplayLabel(destination);
}

function formatCountryName(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;

  if (trimmed.length === 2) {
    const displayNames = new Intl.DisplayNames(["fr"], { type: "region" });
    const resolved = displayNames.of(trimmed.toUpperCase());
    if (resolved) return resolved;
  }

  return trimmed;
}

function resolveCenterFontSize(radius: number, label: string): number {
  if (label.length >= 4) return radius * 0.46;
  if (label.length === 3) return radius * 0.58;
  return radius * 0.72;
}

const MIN_ZOOM = 3;   
const MAX_ZOOM = 24;
const MAX_CLUSTER_ZOOM_LEVEL = 8;
const INITIAL_FIT_PADDING = 12;
const INITIAL_FIT_SCALE_BOOST = 1.65;

function addZoom(
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
  movable: d3.Selection<SVGGElement, unknown, null, undefined>,
  fitBounds: MapBounds | null = null,
  onZoomScaleChange?: (zoomScale: number) => void,
) {
  const zoomed = (event: d3.D3ZoomEvent<SVGSVGElement, unknown>) => {
    movable.attr("transform", event.transform.toString());
    const k = event.transform.k;
    lockMapItemsScale(movable, k);
    onZoomScaleChange?.(k);
  };

  // pan/zoom config
  const bbox = (movable.node() as SVGGElement).getBBox();
  const PAD = 60;
  const contentExtent: [[number, number], [number, number]] = [
    [bbox.x - PAD, bbox.y - PAD],
    [bbox.x + bbox.width + PAD, bbox.y + bbox.height + PAD],
  ];

  const zoom = d3.zoom<SVGSVGElement, unknown>()
    .scaleExtent([MIN_ZOOM, MAX_ZOOM])
    .translateExtent(contentExtent)
    .extent([[0, 0], [WIDTH, HEIGHT]])
    .on("zoom", zoomed);

  svg
    .style("touch-action", "none")
    .on(".zoom", null)
    .call(zoom)
    .on("wheel.zoom-enhanced", (event) => {
      event.preventDefault();
      const direction = event.deltaY < 0 ? 1.2 : 0.8;
      const pointer = d3.pointer(event);
      svg
        .transition()
        .duration(80)
        .call(zoom.scaleBy, direction, pointer);
    });

  const initialBounds = fitBounds ?? getSvgNodeBounds(movable.node() as SVGGElement);
  zoomToBounds(svg, zoom, initialBounds, INITIAL_FIT_PADDING);
}

function getClusterZoomLevel(zoomScale: number): number {
  const level = Math.floor(zoomScale);
  return Math.max(1, Math.min(level, MAX_CLUSTER_ZOOM_LEVEL));
}

function lockMapItemsScale(
  movable: d3.Selection<SVGGElement, unknown, null, undefined>,
  zoomScale: number,
) {
  movable.selectAll<SVGGElement, DonutDatum>("g.donut-item")
    .attr("transform", (d) => `translate(${d.x}, ${d.y}) scale(${1 / zoomScale})`);

  movable.selectAll<SVGGElement, unknown>("g.map-transit-cluster-label")
    .attr("transform", function () {
      return buildMapScaleLockTransform(this, zoomScale);
    });

  movable.selectAll<SVGGElement, unknown>("g.map-transit-cluster-marker")
    .attr("transform", function () {
      return buildMapScaleLockTransform(this, zoomScale);
    });
}

function buildMapScaleLockTransform(node: SVGGElement, zoomScale: number): string | null {
  const item = d3.select(node);
  const x = Number(item.attr("data-x"));
  const y = Number(item.attr("data-y"));
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return `translate(${x}, ${y}) scale(${1 / zoomScale}) translate(${-x}, ${-y})`;
}

function zoomToBounds(
  svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
  zoom: d3.ZoomBehavior<SVGSVGElement, unknown>,
  bounds: MapBounds,
  padding = INITIAL_FIT_PADDING,
) {
  const { x, y, width, height } = bounds;
  if (!isFinite(width) || !isFinite(height) || width === 0 || height === 0) return;

  const sx = WIDTH / (width + padding * 2);
  const sy = HEIGHT / (height + padding * 2);
  // on prend le min pour tout faire tenir, puis on autorise l'utilisateur à zoomer
  const k = Math.max(MIN_ZOOM, Math.min(Math.min(sx, sy) * INITIAL_FIT_SCALE_BOOST, MAX_ZOOM));

  const tx = WIDTH / 2 - k * (x + width / 2);
  const ty = HEIGHT / 2 - k * (y + height / 2);

  const t = d3.zoomIdentity.translate(tx, ty).scale(k);

  svg.call(zoom.transform, t);
}

function getSvgNodeBounds(node: SVGGElement): MapBounds {
  const { x, y, width, height } = node.getBBox();
  return { x, y, width, height };
}
